import { RequestClient, ChainId, ChainIdToChain, LegacyBalance, formatBigIntToSafeValue } from "@doritokit/helpers";

type ChainIdValue = typeof ChainId[keyof typeof ChainId];

interface TokenInfo {
  address?: string;
  decimals: number;
  symbol?: string;
  ticker?: string;
  name?: string;
  chain: string;
}

interface AnkrBalanceAsset {
  balance?: string;
  balanceRaw?: string;
  balanceRawInteger?: string;
  balanceUsd?: string;
  tokenDecimals?: number;
  tokenSymbol?: string;
  tokenName?: string;
  tokenContract?: string;
  contractAddress?: string;
  tokenType?: string;
}

interface AnkrResponse {
  jsonrpc: string;
  id: number;
  result?: {
    totalBalanceUsd?: string;
    totalCount?: number;
    assets?: AnkrBalanceAsset[];
  };
  error?: {
    code: number;
    message: string;
  };
}

const DEFAULT_ANKR_ENDPOINT = "https://rpc.ankr.com/multichain";

const chainIdToAnkrBlockchain: Partial<Record<ChainIdValue, string>> = {
  [ChainId.Ethereum]: "eth",
  [ChainId.Optimism]: "optimism",
  [ChainId.Polygon]: "polygon",
  [ChainId.Arbitrum]: "arbitrum",
  [ChainId.Base]: "base",
  [ChainId.BinanceSmartChain]: "bsc",
  [ChainId.Avalanche]: "avalanche",
  [ChainId.Bitcoin]: "btc",
  [ChainId.Solana]: "solana",
  [ChainId.Polkadot]: "polkadot",
};

const resolveEndpoint = (apiKey?: string, endpoint?: string): string => {
  const trimmedEndpoint = endpoint?.trim();
  if (trimmedEndpoint) {
    return trimmedEndpoint;
  }

  const trimmedKey = apiKey?.trim();
  if (!trimmedKey) {
    return DEFAULT_ANKR_ENDPOINT;
  }

  if (/^https?:\/\//i.test(trimmedKey)) {
    return trimmedKey;
  }

  return `${DEFAULT_ANKR_ENDPOINT}/${trimmedKey}`;
};

const toBigIntString = (asset: AnkrBalanceAsset, decimals: number): string => {
  const rawInt = asset.balanceRawInteger || asset.balanceRaw;
  if (rawInt && rawInt !== "") {
    try {
      return BigInt(rawInt).toString();
    } catch (_e) {
      // Fall through to decimal parsing
    }
  }

  if (!asset.balance) {
    return "0";
  }

  const [integerPart, fractionPart = ""] = asset.balance.split(".");
  const paddedFraction = fractionPart.padEnd(decimals, "0").slice(0, decimals);
  const rawString = `${integerPart}${paddedFraction}`.replace(/^0+/, "");

  if (rawString === "") {
    return "0";
  }

  try {
    return BigInt(rawString).toString();
  } catch (_e) {
    return "0";
  }
};

const createSymbol = (tokenInfo: TokenInfo | undefined, asset: AnkrBalanceAsset): string => {
  const symbol = tokenInfo?.symbol || tokenInfo?.ticker || asset.tokenSymbol || asset.tokenName || "UNKNOWN";
  const contract = asset.contractAddress || asset.tokenContract || tokenInfo?.address;
  return contract ? `${symbol}-${contract}` : symbol;
};

const safeNumber = (value: unknown, fallback = 0): number => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const logDebug = (...args: unknown[]) => {
  if ((window as any)?.__WINBIT32_BALANCE_DEBUG__ === true) {
    console.info("[balanceApi]", ...args);
  }
};

const reportError = (message: string, details?: unknown) => {
  if (details) {
    console.warn(message, details);
  } else {
    console.warn(message);
  }
};

const buildAnkrPayload = (address: string, blockchain: string) => ({
  jsonrpc: "2.0",
  method: "ankr_getAccountBalance",
  params: {
    walletAddress: address,
    blockchain: [blockchain],
    pageSize: 1000,
    onlyWhitelisted: false,
  },
  id: Date.now(),
});

const normalizeBalances = ({
  assets,
  chainId,
  tokens,
}: {
  assets: AnkrBalanceAsset[];
  chainId: ChainIdValue;
  tokens: TokenInfo[];
}): LegacyBalance[] => {
  const chain = ChainIdToChain[chainId];
  const chainUpper = (chain || "").toUpperCase();
  const tokensForChain = tokens.filter(token => (token.chain || "").toUpperCase() === chainUpper);
  const tokenLookup = new Map(
    tokensForChain
      .filter(token => token.address)
      .map(token => [String(token.address).toLowerCase(), token])
  );

  return assets
    .map((asset) => {
      const contract = (asset.contractAddress || asset.tokenContract || "").toLowerCase();
      const tokenInfo = contract ? tokenLookup.get(contract) : tokensForChain.find(token => !token.address);

      const decimals = safeNumber(tokenInfo?.decimals ?? asset.tokenDecimals, 18);
      if (!Number.isFinite(decimals) || decimals < 0) {
        return null;
      }

      let rawValue: bigint;
      try {
        rawValue = BigInt(toBigIntString(asset, decimals));
      } catch (_e) {
        return null;
      }

      if (rawValue === 0n) {
        return null;
      }

      const formattedValue = formatBigIntToSafeValue({
        value: rawValue,
        decimal: decimals,
        bigIntDecimal: decimals,
      });

      return {
        value: formattedValue,
        decimal: decimals,
        chain,
        symbol: createSymbol(tokenInfo, asset),
      } as LegacyBalance;
    })
    .filter((balance): balance is LegacyBalance => Boolean(balance));
};

const balanceApiFactory = ({
  tokens,
  apiKey,
  endpoint,
}: {
  tokens: TokenInfo[];
  apiKey?: string;
  endpoint?: string;
}) => {
  const url = resolveEndpoint(apiKey, endpoint);
  logDebug("Initialised balance API", { url });

  return (chainId: ChainIdValue) => ({
    isAnkrApi: true,
    getBalance: async (address: string): Promise<LegacyBalance[]> => {
      const blockchain = chainIdToAnkrBlockchain[chainId];
      if (!blockchain) {
        reportError(`[balanceApi] No RPC balance support for chainId ${chainId} (${ChainIdToChain[chainId] || "unknown"})`);
        return [];
      }

      logDebug("Fetching balances", { chainId, blockchain, address });

      let response: AnkrResponse | null = null;
      try {
        response = await RequestClient.post<AnkrResponse>(
          url,
          {
            body: JSON.stringify(buildAnkrPayload(address, blockchain)),
            headers: {
              "content-type": "application/json",
            },
          }
        );
      } catch (error) {
        reportError(`[balanceApi] Request failed for ${blockchain}. Returning empty balances.`, error);
        return [];
      }

      if (!response) {
        reportError(`[balanceApi] Empty response for ${blockchain}.`);
        return [];
      }

      if (response.error) {
        reportError(`[balanceApi] RPC error for ${blockchain}: code=${response.error.code} message=${response.error.message}`, response.error.data);
        return [];
      }

      const assets = Array.isArray(response.result?.assets) ? response.result!.assets! : [];
      if (assets.length === 0) {
        logDebug("No assets returned", { blockchain, address });
        return [];
      }

      const balances = normalizeBalances({ assets, chainId, tokens });
      logDebug("Balances normalised", { blockchain, address, count: balances.length });
      return balances;
    },
  });
};

export const balanceApi = balanceApiFactory;
export const ankrApi = balanceApiFactory;
export const alchemyApi = balanceApiFactory; // Legacy alias for existing imports
